import React, { useState, useEffect } from 'react';
import { AuthService } from '../services/authService';

interface LoginProps {
  onLoginSuccess: (response: any) => void;
  onRememberMeChange?: (remember: boolean) => void;
}

const ENTIK_MEDIA_LOGO_OPTIMOLE =
  'https://mlty5i8mxpr9.i.optimole.com/w:411/h:91/q:mauto/f:best/https://entikmedia.com/wp-content/uploads/2023/12/cropped-cropped-logo_entik_media_web.jpg';

const Login: React.FC<LoginProps> = ({ onLoginSuccess, onRememberMeChange }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setAccessDenied(false);

    try {
      const response = await AuthService.login({ email, password });

      if (!response.success) {
        const isPlatformDenied =
          response.code === 'PLATFORM_ACCESS_DENIED' ||
          (response.message || '').toLowerCase().includes('administrador');
        setAccessDenied(isPlatformDenied);
        setError(response.message || 'Credenciales incorrectas');
        return;
      }

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberMe');
      }

      onRememberMeChange?.(rememberMe);
      onLoginSuccess(response);
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#F6F9FC] dark:bg-[#0d1117]"
      style={{ minHeight: '100dvh' }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#635BFF]/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-[#635BFF]/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-[400px] px-4">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#635BFF] mb-4 shadow-stripe-md">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-white">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#0A2540] dark:text-[#e6edf3]">FactuApp</h1>
          <p className="mt-1 text-sm text-[#425466] dark:text-[#8b949e]">Inicia sesión en tu cuenta</p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#21262d] rounded-2xl shadow-stripe p-8">
          {/* Entik logo */}
          <div className="flex justify-center mb-6">
            <img
              src={ENTIK_MEDIA_LOGO_OPTIMOLE}
              alt="Entik Media"
              className="h-10 w-auto max-w-full opacity-80"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#0A2540] dark:text-[#e6edf3] mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E6E9F0] dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] placeholder-[#8898aa] focus:outline-none focus:ring-2 focus:ring-[#635BFF]/25 focus:border-[#635BFF] transition-colors text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-[#0A2540] dark:text-[#e6edf3] mb-1.5">
                  Contraseña
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E6E9F0] dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] placeholder-[#8898aa] focus:outline-none focus:ring-2 focus:ring-[#635BFF]/25 focus:border-[#635BFF] transition-colors text-sm"
                  placeholder="••••••••"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setRememberMe(v);
                    onRememberMeChange?.(v);
                  }}
                  className="h-4 w-4 rounded border-[#E6E9F0] text-[#635BFF] focus:ring-[#635BFF]/30 accent-[#635BFF]"
                />
                <label htmlFor="remember-me" className="text-sm text-[#425466] dark:text-[#8b949e]">
                  Recordar usuario
                </label>
              </div>
            </div>

            {accessDenied ? (
              <div className="rounded-xl border border-[#E6E9F0] dark:border-[#30363d] bg-[#F6F9FC] dark:bg-[#0d1117] p-5 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#0A2540] dark:bg-[#e6edf3]">
                  <svg className="h-5 w-5 text-white dark:text-[#0A2540]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3]">Acceso no disponible</p>
                <p className="mt-1.5 text-xs text-[#425466] dark:text-[#8b949e]">
                  {error || 'Contacta con el administrador'}
                </p>
                <p className="mt-2 text-xs text-[#8898aa]">
                  Tu suscripción puede estar pausada o fuera del periodo contratado.
                </p>
              </div>
            ) : error ? (
              <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center gap-2 py-2.5 px-4 rounded-lg bg-[#635BFF] text-white text-sm font-semibold hover:bg-[#4F46E5] focus:outline-none focus:ring-2 focus:ring-[#635BFF]/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Iniciando sesión...
                </>
              ) : 'Iniciar sesión'}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-xs text-[#8898aa]">
          Plataforma de facturación profesional
        </p>
      </div>
    </div>
  );
};

export default Login;
export { Login };
